import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
  faEye,
  faEyeSlash,
  faInfoCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import {
  createAuthenticatedAxiosInstance,
  fetchUserById,
  updateUser,
} from "../../api/auth";
import logo from "assets/logo.png";
import "./UserDetails.scss";
import { useAuthStore } from "../../zustand/loginStore";
import CustomNavbar from "./CustomNavbar";
import LoadingScreen from "../general/LoadingScreen";
import CancelContinue from "../general/CancelContinueModal";
import { useUserStore } from "../../zustand/userStore";
import ConfirmationModal from "../general/ConfirmationModal";
import CustomSelect from "../general/CustomSelect";
import CustomBackButton from "components/general/CustomBackButton";

interface User {
  id: number;
  name: string;
  email: string;
  password: string;
  role: string;
  created_at: string;
  status: boolean;
  updated_at: string;
}

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const { token, role } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [showModal, setShowModal] = useState(false);
  const { userIdSelected, isEdited, setEdited } = useUserStore();
  const [isEditing, setIsEditing] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [emailHasReadyExists, setEmailHasReadyExists] = useState(false);
  const [selectedRole, setSelectedRole] = useState<{
    value: string;
    label: string;
  } | null>(null);
  // Define the options
  const roleOptions = useMemo(
    () => [
      { value: "gerente", label: "Gerente" },
      { value: "asistente", label: "Asistente" },
    ],
    []
  );

  /**
   * Asynchronous function to load user data
   * @async
   * @function loadUser
   * @description Fetches user data by ID and updates the component state
   */
  const loadUser = useCallback(async () => {
    try {
      const userData: User = await fetchUserById(
        axiosInstance,
        Number(userIdSelected)
      );
      setUser(userData);
    } catch (error) {
      console.error("Error fetching user:", error);
    } finally {
      setIsLoading(false);
    }
  }, [userIdSelected]);

  /**
   * Effect hook to load user data when the component mounts or when isEdited changes
   * @effect
   * @description Calls loadUser function when the component mounts or isEdited state changes
   */
  useEffect(() => {
    loadUser();
  }, [ isEdited]);

  /**
   * Function to handle toggling the status modal
   * @function handleToggleStatus
   * @description Sets the showModal state to true, displaying the status toggle modal
   */
  const handleToggleStatus = () => {
    setShowModal(true);
  };

  /**
   * Asynchronous function to handle confirming the status toggle
   * @async
   * @function handleConfirmToggle
   * @description Updates the user's status, updates the component state, and closes the modal
   */
  const handleConfirmToggle = async () => {
    if (!user || !userIdSelected) return;

    try {
      const { data: updatedUser, status } = await updateUser(
        axiosInstance,
        userIdSelected,
        {
          status: !user.status,
        }
      );
      if (status === 200) {
        setUser(updatedUser);
        setEdited(!isEdited);
        setShowModal(false);
        setShowSuccessModal(true);
      } else {
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error toggling user status:", error);
      setShowErrorModal(true);
    }
  };

  /**
   * Asynchronous function to handle saving changes to user data
   * @async
   * @function handleSaveChanges
   * @description Validates input, updates user data, and resets relevant component states
   */
  const handleSaveChanges = async () => {
    if (!user) return;
    if (user.password && user.password.length < 8) {
      setPasswordError("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (user.password && user.password !== confirmPassword) {
      setPasswordError("Las contraseñas no coinciden");
      return;
    }
    try {
      const { data: updatedUser, status } = await updateUser(
        axiosInstance,
        user.id,
        user
      );

      if (status === 200) {
        setUser(updatedUser);
        setIsEditing(false);
        setPasswordError("");
        setConfirmPassword("");
        setPasswordTouched(false);
        loadUser();
        setShowSuccessModal(true);
      }
    } catch (error: any) {
      setShowErrorModal(true);
      if (
        error?.response?.data.errors.email[0] ===
        "The email has already been taken."
      ) {
        setEmailHasReadyExists(true);
      }
      console.error(
        "Error updating user:",
        error?.response?.data.errors.email[0] || error
      );
    }
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    setIsEditing(false);
    setPasswordTouched(false);
    loadUser();
    setShowCancelModal(false);
  };

  /**
   * Handler function to navigate back to the dashboard
   *
   * @function
   * @name handleBack
   * @description Navigates to the dashboard with the "usuarios" hash
   */
  const handleBack = () => {
    navigate("/dashboard#usuarios");
  };

  /**
   * Effect hook to update the selected role when the user data changes
   *
   * @effect
   * @dependencies [user]
   */
  useEffect(() => {
    if (user) {
      setSelectedRole(
        roleOptions.find((option) => option.value === user.role) || null
      );
    }
  }, [user, roleOptions]);

  return (
    <>
      <CustomNavbar />
      <Container className="user-details">
        <Row className="mb-3">
          <CustomBackButton onClick={handleBack} />

          <Col xs={12} className="text-center ">
            <img src={logo} alt="Logo" className="logo" />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="text-center">
            <h1>{isEditing ? "Editar usuario" : "Información de usuario"}</h1>
          </Col>
        </Row>
        {isLoading ? (
          <LoadingScreen loadingText="Cargando información del usuario" />
        ) : (
          <Row className="justify-content-center">
            <Col md={8} className="user-info-container">
              <Row className="mb-3">
                <Col xs={6}>
                  <Form.Label className="fw-bold">No.</Form.Label>
                  <div className="d-flex align-items-center">
                    <span style={{ fontSize: "24px", color: "#747775" }}>
                      {user?.id}
                    </span>
                    <OverlayTrigger
                      trigger={["hover", "focus"]}
                      placement="right"
                      overlay={
                        <Tooltip className="custom-tooltip">
                          ID único del usuario
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faInfoCircle} className="ms-2" />
                    </OverlayTrigger>
                  </div>
                </Col>
                <Col xs={6} className="text-end">
                  <OverlayTrigger
                    placement="left"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Estado actual del usuario
                      </Tooltip>
                    }
                  >
                    <span
                      className={`status-badge ${
                        user?.status ? "active" : "inactive"
                      }`}
                    >
                      {user?.status ? "Activo" : "Inactivo"}
                    </span>
                  </OverlayTrigger>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Nombre:</Form.Label>
                <Form.Control
                  type="text"
                  style={{ color: !isEditing ? "#ADB5BD" : "black" }}
                  value={user?.name}
                  readOnly={!isEditing}
                  onChange={(e) =>
                    setUser((prev) =>
                      prev ? { ...prev, name: e.target.value } : null
                    )
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Correo electrónico:</Form.Label>
                <Form.Control
                  type="email"
                  style={{ color: !isEditing ? "#ADB5BD" : "black" }}
                  value={user?.email}
                  readOnly={!isEditing}
                  onChange={(e) =>
                    setUser((prev) =>
                      prev ? { ...prev, email: e.target.value } : null
                    )
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Puesto</Form.Label>
                {isEditing ? (
                  <CustomSelect
                    options={roleOptions}
                    value={
                      selectedRole
                        ? {
                            ...selectedRole,
                            label:
                              selectedRole.label.charAt(0).toUpperCase() +
                              selectedRole.label.slice(1),
                          }
                        : null
                    }
                    placeholder="Puesto"
                    onChange={(option) => {
                      if (option) {
                        setSelectedRole(option);
                        setUser((prev) =>
                          prev ? { ...prev, role: option.value } : null
                        );
                      }
                    }}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    style={{ color: "#ADB5BD" }}
                    value={
                      user?.role
                        ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
                        : ""
                    }
                    readOnly
                  />
                )}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Fecha de creación:</Form.Label>
                <Form.Control
                  type="text"
                  style={{ color: "#ADB5BD" }}
                  value={user ? new Date(user.created_at).toLocaleString() : ""}
                  readOnly
                />
                {isEditing && role === "admin" && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Nueva contraseña:</Form.Label>
                      <div className="password-input-container">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          style={{
                            paddingRight: "40px",
                            backgroundImage: "none",
                          }}
                          placeholder="Ingrese nueva contraseña"
                          onChange={(e) => {
                            const newPassword = e.target.value;
                            setPasswordTouched(true);

                            if (
                              newPassword.length >= 8 ||
                              newPassword.length === 0
                            ) {
                              setUser((prev) =>
                                prev ? { ...prev, password: newPassword } : null
                              );
                              setPasswordError("");
                            } else {
                              setPasswordError(
                                "La contraseña debe tener al menos 8 caracteres"
                              );
                            }
                          }}
                          isInvalid={!!passwordError}
                        />
                        <Button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                            color="black"
                          />
                        </Button>
                      </div>
                      <Form.Control.Feedback
                        type="invalid"
                        style={{ display: passwordError ? "block" : "none" }}
                      >
                        {passwordError}
                      </Form.Control.Feedback>
                    </Form.Group>
                    {passwordTouched && (
                      <Form.Group className="mb-3">
                        <Form.Label>Confirmar Nueva Contraseña</Form.Label>
                        <div className="password-input-container">
                          <Form.Control
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirme la nueva contraseña"
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            isInvalid={confirmPassword !== user?.password}
                            style={{
                              paddingRight: "40px",
                              backgroundImage: "none",
                            }}
                          />
                          <Button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            <FontAwesomeIcon
                              icon={showConfirmPassword ? faEyeSlash : faEye}
                              color="black"
                            />
                          </Button>
                        </div>
                        <Form.Control.Feedback
                          type="invalid"
                          style={{ display: passwordError ? "block" : "none" }}
                        >
                          Las contraseñas no coinciden
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                  </>
                )}
              </Form.Group>
              {!isEditing ? (
                <Row className="mt-4">
                  <Col xs={6}>
                    <Button
                      className="bg-secundary"
                      variant="primary"
                      onClick={handleToggleStatus}
                    >
                      {user?.status ? "Desactivar usuario" : "Activar usuario"}
                    </Button>
                  </Col>
                  <Col xs={6} className="text-end">
                    <Button
                      variant="primary"
                      onClick={() => setIsEditing(true)}
                    >
                      Editar
                    </Button>
                  </Col>
                </Row>
              ) : (
                <Row className="mt-4">
                  <Col xs={6}>
                    <Button
                      className="bg-secundary"
                      variant="primary"
                      onClick={handleCancelClick}
                    >
                      Cancelar
                    </Button>
                  </Col>
                  <Col xs={6} className="text-end">
                    <Button variant="primary" onClick={handleSaveChanges}>
                      Guardar cambios
                    </Button>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        )}
      </Container>
      <CancelContinue
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={handleConfirmToggle}
        title={`${user?.status ? "Desactivar" : "Activar"} usuario`}
        message={`¿Estás seguro que deseas ${
          user?.status ? "desactivar" : "activar"
        } este usuario?`}
        icon={faExclamationTriangle}
        iconColor="text-danger"
      />
      <CancelContinue
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleConfirmCancel}
        title="Atención"
        message="¿Estás seguro que deseas cancelar la edición? Los cambios sin guardar no sufrirán efecto."
        icon={faExclamationTriangle}
        iconColor="text-danger"
      />
      <ConfirmationModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        title="Confirmación"
        message="Los cambios se han guardado con éxito"
        icon={faCheckCircle}
        iconColor="text-success"
      />

      <ConfirmationModal
        show={showErrorModal}
        onHide={() => {
          setShowErrorModal(false);
          setEmailHasReadyExists(false);
        }}
        title="Error"
        message={
          emailHasReadyExists
            ? "Este correo ya está en uso.\nPor favor, utiliza otro correo."
            : "Error al conectar con el servidor.\nInténtalo más tarde."
        }
        icon={faTimesCircle}
        iconColor="text-danger"
      />
    </>
  );
};

export default UserDetails;
